import SpaceBetween from '@risk-smart/themed-cloudscape-components/space-between';
import Button from '@risksmart-app/components/src/button';
import Table from '@risksmart-app/components/src/table';
import { ParentTypes as Parent_Type_Enum } from '@risksmart-app/domain/src/types/consts/parent-type';
import { type FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import ExportButton from 'src/components/export-button';
import InstantiateEnterpriseRiskModal from 'src/components/instantiate-enterprise-risk-modal/InstantiateEnterpriseRiskModal';
import { useGetEnterpriseRisksRegister } from 'src/hooks/queries/enterprise-risk/useGetEnterpriseRisksRegister';
import { PageLayout } from 'src/layouts';
import { Permission } from 'src/rbac/Permission';

import CustomisableRibbon from '@/components/customisable-ribbon/CustomisableRibbon';
import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';
import { useRibbonAndExport } from '@/hooks/useRibbonAndExport';
import { getCounter } from '@/utils/collectionUtils';
import { addEnterpriseRiskUrl } from '@/utils/urls';

import { useGetCollectionTableProps } from './config';
import { useGetDefaultRibbonFilters } from './defaultRibbonFilters';
import { EnterpriseRiskRegisterToolbar } from './EnterpriseRiskRegisterToolbar';
import type { EnterpriseRiskRegisterFields } from './types';

const EnterpriseRiskPage: FC = () => {
  const { t } = useTranslation(['common'], { keyPrefix: 'enterpriseRisks' });
  const objectToolbarEnabled = useIsFeatureFlagEnabled('dynamic_forms');
  const navigate = useNavigate();

  const { data, loading } = useGetEnterpriseRisksRegister({ queryArgs: {} });

  const tableProps = useGetCollectionTableProps(data?.enterprise_risk);

  const { ribbonProps, ribbonExportProps } = useRibbonAndExport(
    useGetDefaultRibbonFilters
  );

  const [selectedItems, setSelectedItems] = useState<
    Array<EnterpriseRiskRegisterFields>
  >([]);

  const [showInstantiateModal, setShowInstantiateModal] = useState(false);

  const counter = getCounter(tableProps.totalItemsCount, loading);

  const title = t('registerTitle');

  return (
    <PageLayout
      title={title}
      helpTranslationKey={'enterpriseRisks.registerHelp'}
      counter={counter}
      // Object-level toolbar (CUS-167) — flag-gated. Mounted only when the flag
      // is on, so `useExportOptions` runs on exactly one path (toolbar vs the
      // legacy ExportButton) rather than both.
      headerOverride={
        objectToolbarEnabled ? (
          <EnterpriseRiskRegisterToolbar
            title={title}
            counter={counter}
            tableProps={tableProps}
            getActiveRibbonFilters={ribbonExportProps.getActiveRibbonFilters}
            onCreate={() => navigate(addEnterpriseRiskUrl())}
            onInstantiate={() => setShowInstantiateModal(true)}
            instantiateDisabled={selectedItems.length === 0}
          />
        ) : undefined
      }
      actions={
        <SpaceBetween direction={'horizontal'} size={'xs'}>
          <ExportButton
            tableProps={tableProps}
            entityLabel={title}
            {...ribbonExportProps}
          />
          <Permission permission={'insert:enterprise_risk'}>
            <Button variant={'primary'} href={addEnterpriseRiskUrl()}>
              {t('createNewButton')}
            </Button>
          </Permission>
          <Permission permission={'insert:enterprise_risk'}>
            <Button
              variant={selectedItems.length === 0 ? undefined : 'primary'}
              disabled={!selectedItems.length}
              onClick={() => setShowInstantiateModal(true)}
            >
              {t('instantiateButton')}
            </Button>
          </Permission>
        </SpaceBetween>
      }
    >
      <CustomisableRibbon
        items={tableProps.allItems}
        loading={loading}
        propertyFilterQuery={tableProps.propertyFilterQuery}
        onFilterQueryChanged={tableProps.actions.setPropertyFiltering}
        filteringProperties={tableProps.filteringProperties}
        filteringOptions={tableProps.propertyFilterProps.filteringOptions}
        parentType={Parent_Type_Enum.EnterpriseRisk}
        {...ribbonProps}
        relocateEditToToolbar={objectToolbarEnabled}
      />
      <Table
        {...tableProps}
        data-testid={'enterprise-risk-table'}
        trackBy={'Id'}
        loading={loading}
        selectedItems={selectedItems}
        selectionType={'multi'}
        onSelectionChange={({ detail }) => {
          setSelectedItems(detail.selectedItems);
        }}
      />
      {showInstantiateModal && (
        <InstantiateEnterpriseRiskModal
          isVisible={showInstantiateModal}
          onDismiss={() => {
            setShowInstantiateModal(false);
          }}
          enterpriseRiskIds={selectedItems.map((si) => si.Id)}
        />
      )}
    </PageLayout>
  );
};

export default EnterpriseRiskPage;
